import React, { useState, useRef, useEffect, useCallback } from "react";
import "./SchoolLetterHeader.css";
import "primeicons/primeicons.css";
import apiClient from "../../stateManagers/services/axiosInterceptor";

import type {
  CanvasElement,
  TextElement,
  ImageElement,
  ElementPatch,
  Align,
  LockedBlock,
} from "./SchoolLetterHeader";

import {
  PAGE_W,
  PAGE_H,
  SNAP,
  FONT_SIZES,
  SCHOOL,
  ZOOM_STEPS,
} from "./SchoolLetterHeader";

// ── Template types ─────────────────────────────────────────────────────────────
interface TemplateSummary {
  id?: string | number;
  _id?: string;
  template_id?: string;
  template_type?: string;
  title: string;
  description?: string;
  is_active?: number;
  created_by: string;
  updated_at: string;
}

interface TemplateDetail extends TemplateSummary {
  elements: CanvasElement[];
  locked_blocks?: LockedBlock[];
}

interface LetterEditorProps {
  elements: CanvasElement[];
  selId: string | null;
  zoom: number;
  letterheadTitle: string;
  printing: boolean;
  onTitleChange: (title: string) => void;
  onAddEl: (o?: Partial<TextElement>) => void;
  onAddImgEl: (o?: Partial<ImageElement>) => void;
  onUpdateEl: (id: string, patch: ElementPatch) => void;
  onDeleteEl: (id: string) => void;
  onDuplicateEl: (id: string) => void;
  onSaveAndPrint: () => void;
  onSetSelId: (id: string | null) => void;
  SCHOOL: typeof SCHOOL;
  PAGE_W: number;
  PAGE_H: number;
  SNAP: number;
  FONT_SIZES: number[];
  isEditing?: boolean;
  onNewLetterhead?: () => void;
  lockedBlocks?: LockedBlock[];
  onUpdateLockedBlock?: (index: number, x: number, y: number, width?: number) => void;
  currentLetterheadId: string;
  isDirty?: boolean;
  onPrintOnly?: () => void;
  onZoomChange?: (zoom: number) => void;
  titleBarLabel?: string;
  onApplyTemplate?: (elements: CanvasElement[]) => void;
  isTemplateMode?: boolean;
  onSaveOnly?: () => void;
  hideHeader?: boolean;
  hideFooter?: boolean;
  onHideHeaderChange?: (v: boolean) => void;
  onHideFooterChange?: (v: boolean) => void;
}

interface EditableTextProps {
  el: TextElement;
  isSelected: boolean;
  onUpdate: (id: string, patch: ElementPatch) => void;
  savedSelRef: React.MutableRefObject<Range | null>;
}

const TEMPLATE_TYPE_COLORS: Record<string, string> = {
  circular: "linear-gradient(135deg,#7c3aed,#a78bfa)",
  notice: "linear-gradient(135deg,#0369a1,#38bdf8)",
  warning: "linear-gradient(135deg,#b45309,#fcd34d)",
  permission: "linear-gradient(135deg,#065f46,#34d399)",
  general: "linear-gradient(135deg,#4169E1,#5B7CE6)",
};

const typeGrad = (t?: string) =>
  TEMPLATE_TYPE_COLORS[t?.toLowerCase() ?? ""] ?? TEMPLATE_TYPE_COLORS.general;

// ── Helper function to get template identifier ──────────────────────────────
const getTemplateIdentifier = (template: TemplateSummary): string => {
  return String(template.template_id || template._id || template.id || '');
};

// ── EditableText ───────────────────────────────────────────────────────────────
const EditableText: React.FC<EditableTextProps> = ({ el, isSelected, onUpdate, savedSelRef }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<string>("");

  useEffect(() => {
    if (!divRef.current) return;
    if (lastIdRef.current === el.id) return;
    lastIdRef.current = el.id;
    divRef.current.innerHTML = el.html || el.content;
  }, [el.id, el.html, el.content]);

  return (
    <div
      ref={divRef}
      className="el-text"
      contentEditable
      suppressContentEditableWarning
      style={{
        fontSize: el.fontSize,
        fontFamily: el.fontFamily,
        color: el.color,
        fontWeight: el.bold ? "bold" : "normal",
        fontStyle: el.italic ? "italic" : "normal",
        textDecoration: el.underline ? "underline" : "none",
        textAlign: el.align,
      }}
      onSelect={() => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          savedSelRef.current = sel.getRangeAt(0).cloneRange();
        }
      }}
      onInput={(e) => {
        const target = e.target as HTMLDivElement;
        onUpdate(el.id, {
          html: target.innerHTML,
          content: target.innerText,
        });
      }}
    />
  );
};

// ── Template Drawer ────────────────────────────────────────────────────────────
interface TemplateDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (elements: CanvasElement[]) => void;
  onApplied?: () => void;
}

const TemplateDrawer: React.FC<TemplateDrawerProps> = ({ open, onClose, onApply, onApplied }) => {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async (p = 1, type = filterType) => {
    setLoading(true);
    try {
      const typeParam = type !== "all" ? `&template_type=${type}` : "";
      const res = await apiClient.get(`/api/letterhead-templates?page=${p}&limit=12${typeParam}`);
      if (res.data.status === "success") {
        const templatesWithId = (res.data.templates || []).map((t: any) => ({
          ...t,
          id: t.id || t._id || t.template_id || String(Date.now() + Math.random()),
        }));
        setTemplates(templatesWithId);
        setTotalPages(res.data.pagination?.total_pages ?? 1);
        setPage(p);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    if (open) fetchTemplates(1, filterType);
  }, [open, fetchTemplates, filterType]);

  const handleFilterChange = (type: string) => {
    setFilterType(type);
    fetchTemplates(1, type);
  };

  const applyTemplate = async (templateId: string) => {
    setApplying(templateId);
    try {
      const template = templates.find(t => getTemplateIdentifier(t) === templateId);
      const idToUse = template?.template_id || templateId;

      const res = await apiClient.get(`/api/letterhead-templates?template_id=${idToUse}`);
      if (res.data.status === "success" && res.data.template) {
        const t: TemplateDetail = res.data.template;
        const processedElements = (t.elements || []).map((el: any) => {
          const newId = `el${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
          if (el.type === "text" && !el.html) return { ...el, id: newId, html: el.content || "" };
          return { ...el, id: newId };
        });
        onApply(processedElements);
        (onApplied ?? onClose)();
      }
    } catch (error) {
      console.error('❌ Failed to apply template:', error);
    } finally {
      setApplying(null);
      setConfirmId(null);
    }
  };

  const FILTER_TYPES = [
    { value: "all", label: "All" },
    { value: "general", label: "General" },
    { value: "circular", label: "Circular" },
    { value: "notice", label: "Notice" },
    { value: "warning", label: "Warning" },
    { value: "permission", label: "Permission" },
  ];

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 8998,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "all" : "none",
          transition: "opacity 0.22s",
        }}
      />
      <div style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 360,
        height: "100vh",
        background: "white",
        boxShadow: "-8px 0 40px rgba(30,58,138,0.18)",
        zIndex: 8999,
        display: "flex",
        flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.26s cubic-bezier(0.4,0,0.2,1)",
        fontFamily: "'DM Sans',sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 16px",
          borderBottom: "1px solid #e5e7eb",
          background: "linear-gradient(135deg,#1e3a8a,#3b82f6)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="pi pi-copy" style={{ marginRight: 7, fontSize: 13 }} />
              Letter Templates
            </div>
            <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>
              Click a template to load its elements
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: "rgba(255,255,255,0.18)",
              border: "none",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="pi pi-times" style={{ fontSize: 12 }} />
          </button>
        </div>

        {/* Filter chips */}
        <div style={{
          padding: "8px 12px",
          borderBottom: "1px solid #f0f4f8",
          display: "flex",
          gap: 5,
          flexWrap: "wrap",
          flexShrink: 0,
          background: "#fafbff",
        }}>
          {FILTER_TYPES.map((f) => (
            <button
              key={f.value}
              onClick={() => handleFilterChange(f.value)}
              style={{
                padding: "3px 10px",
                borderRadius: 20,
                border: "1px solid",
                borderColor: filterType === f.value ? "#1e3a8a" : "#e5e7eb",
                background: filterType === f.value
                  ? "linear-gradient(135deg,#1e3a8a,#3b82f6)"
                  : "white",
                color: filterType === f.value ? "white" : "#374151",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                transition: "all 0.13s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 180, gap: 10 }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 22, color: "#2855ae" }} />
              <span style={{ color: "#6b7280", fontSize: 13 }}>Loading templates…</span>
            </div>
          ) : templates.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px 20px", color: "#9ca3af" }}>
              <i className="pi pi-inbox" style={{ fontSize: 28, marginBottom: 10, display: "block" }} />
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>No templates found</div>
              <div style={{ fontSize: 11 }}>Create one via Letter Templates in the sidebar</div>
            </div>
          ) : (
            templates.map((t) => {
              const templateIdentifier = getTemplateIdentifier(t);
              return (
                <div
                  key={templateIdentifier}
                  style={{
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    marginBottom: 8,
                    overflow: "hidden",
                    transition: "box-shadow 0.15s, border-color 0.15s",
                    background: "white",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 16px rgba(30,58,138,0.12)";
                    (e.currentTarget as HTMLDivElement).style.borderColor = "#bfdbfe";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "";
                    (e.currentTarget as HTMLDivElement).style.borderColor = "#e5e7eb";
                  }}
                >
                  <div style={{
                    background: typeGrad(t.template_type),
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "rgba(255,255,255,0.22)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "white",
                      flexShrink: 0,
                    }}>
                      {t.title.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "white",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {t.title}
                      </div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.75)", marginTop: 1 }}>
                        {t.template_type ? t.template_type.charAt(0).toUpperCase() + t.template_type.slice(1) : "General"}
                        {t.template_id && <span style={{ marginLeft: 6, opacity: 0.65 }}>{t.template_id}</span>}
                      </div>
                    </div>
                    {t.is_active ? (
                      <span style={{
                        fontSize: 9,
                        fontWeight: 800,
                        padding: "2px 6px",
                        borderRadius: 10,
                        background: "rgba(16,185,129,0.25)",
                        color: "#ecfdf5",
                        border: "1px solid rgba(16,185,129,0.3)",
                        flexShrink: 0,
                      }}>
                        ACTIVE
                      </span>
                    ) : null}
                  </div>

                  <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      By <strong>{t.created_by}</strong> ·{" "}
                      {new Date(t.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </div>

                    {confirmId === templateIdentifier ? (
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        <button
                          onClick={() => setConfirmId(null)}
                          style={{
                            padding: "4px 9px",
                            borderRadius: 6,
                            border: "1px solid #e5e7eb",
                            background: "white",
                            color: "#374151",
                            fontSize: 11,
                            cursor: "pointer",
                            fontFamily: "'DM Sans',sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          disabled={applying === templateIdentifier}
                          onClick={() => applyTemplate(templateIdentifier)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: "linear-gradient(135deg,#1e3a8a,#3b82f6)",
                            color: "white",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: applying === templateIdentifier ? "default" : "pointer",
                            opacity: applying === templateIdentifier ? 0.7 : 1,
                            fontFamily: "'DM Sans',sans-serif",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          {applying === templateIdentifier
                            ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 11 }} /> Loading…</>
                            : "✓ Confirm"
                          }
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmId(templateIdentifier)}
                        style={{
                          padding: "4px 12px",
                          borderRadius: 6,
                          border: "1px solid #bfdbfe",
                          background: "rgba(59,130,246,0.07)",
                          color: "#1e3a8a",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "'DM Sans',sans-serif",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          flexShrink: 0,
                        }}
                      >
                        <i className="pi pi-download" style={{ fontSize: 11 }} /> Use
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination footer */}
        {totalPages > 1 && (
          <div style={{
            padding: "10px 12px",
            borderTop: "1px solid #f0f4f8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            background: "#fafbff",
          }}>
            <button
              disabled={page <= 1 || loading}
              onClick={() => fetchTemplates(page - 1)}
              style={{
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: "white",
                color: "#374151",
                fontSize: 11,
                fontWeight: 600,
                cursor: page <= 1 ? "default" : "pointer",
                opacity: page <= 1 ? 0.4 : 1,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>
              Page {page} / {totalPages}
            </span>
            <button
              disabled={page >= totalPages || loading}
              onClick={() => fetchTemplates(page + 1)}
              style={{
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: "white",
                color: "#374151",
                fontSize: 11,
                fontWeight: 600,
                cursor: page >= totalPages ? "default" : "pointer",
                opacity: page >= totalPages ? 0.4 : 1,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ── Main LetterEditor ──────────────────────────────────────────────────────────
const LetterEditor: React.FC<LetterEditorProps> = ({
  elements,
  selId,
  zoom,
  letterheadTitle,
  printing,
  onTitleChange,
  onAddEl,
  onAddImgEl,
  onUpdateEl,
  onDeleteEl,
  onDuplicateEl,
  onSaveAndPrint,
  onSetSelId,
  SCHOOL,
  PAGE_W,
  PAGE_H,
  SNAP,
  FONT_SIZES,
  isEditing = false,
  onNewLetterhead,
  lockedBlocks = [],
  onUpdateLockedBlock,
  currentLetterheadId,
  isDirty = false,
  onPrintOnly,
  onZoomChange,
  titleBarLabel,
  onApplyTemplate,
  isTemplateMode = false,
  onSaveOnly,
  hideHeader = false,
  hideFooter = false,
  onHideHeaderChange,
  onHideFooterChange,
}) => {
  const [selLockedIdx, setSelLockedIdx] = useState<number | null>(null);
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState<CanvasElement[] | null>(null);

  const sel = elements.find((e) => e.id === selId) ?? null;
  const pageRef = useRef<HTMLDivElement>(null);
  const headerImgRef = useRef<HTMLImageElement>(null);
  const footerImgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const savedSelRef = useRef<Range | null>(null);
  const sigFileRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<any>(null);
  const resizeRef = useRef<any>(null);
  const lockedDragRef = useRef<any>(null);

  const patchSel = (p: ElementPatch) => { if (selId) onUpdateEl(selId, p); };

  const handleFontSizeChange = (size: number) => {
    if (!selId || !sel || sel.type === "image") return;
    patchSel({ fontSize: size });
    if (savedSelRef.current) {
      const s = window.getSelection()!;
      s.removeAllRanges();
      s.addRange(savedSelRef.current);
    }
  };

  const execOnSel = (cmd: string, value?: string) => {
    if (savedSelRef.current) {
      const s = window.getSelection()!;
      s.removeAllRanges();
      s.addRange(savedSelRef.current);
    }
    document.execCommand(cmd, false, value);
    const f = document.activeElement as HTMLElement | null;
    if (f?.classList.contains("el-text") && selId) {
      onUpdateEl(selId, {
        html: (f as HTMLDivElement).innerHTML,
        content: (f as HTMLDivElement).innerText,
      });
    }
  };

  const handleSigFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (selId && sel?.type === "image") onUpdateEl(selId, { src: ev.target!.result as string });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const addSignOff = () => {
    const textEl: Partial<TextElement> = {
      content: "Yours sincerely,\n\n\n\n________________________\nClass Teacher",
      html: "Yours sincerely,\n\n\n\n________________________\nClass Teacher",
      y: 680,
      fontSize: 12,
    };
    const imgEl: Partial<ImageElement> = { x: 80, y: 710, width: 180, height: 70 };
    onAddEl(textEl);
    onAddImgEl(imgEl);
  };

  const handleTemplateApply = (newElements: CanvasElement[]) => {
    if (elements.length > 0) {
      setReplaceConfirm(newElements);
    } else {
      doApplyTemplate(newElements);
    }
  };

  const doApplyTemplate = (newElements: CanvasElement[]) => {
    if (onApplyTemplate) {
      onApplyTemplate(newElements);
    }
    setReplaceConfirm(null);
    onSetSelId(null);
  };

  // ── Mouse handlers ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();

      if (dragRef.current) {
        const { id, offX, offY, zoom: z } = dragRef.current;
        const newX = Math.round(Math.max(0, Math.min((e.clientX - rect.left) / z - offX, PAGE_W - 40)));
        const newY = Math.round(Math.max(0, Math.min((e.clientY - rect.top) / z - offY, PAGE_H - 20)));
        const d = pageRef.current.querySelector<HTMLElement>(`[data-id="${id}"]`);
        if (d) { d.style.left = `${newX}px`; d.style.top = `${newY}px`; }
        dragRef.current.lastX = newX;
        dragRef.current.lastY = newY;
      }

      if (lockedDragRef.current) {
        const { index, offX, offY, zoom: z } = lockedDragRef.current;
        const newX = Math.round(Math.max(0, (e.clientX - rect.left) / z - offX));
        const newY = Math.round(Math.max(0, (e.clientY - rect.top) / z - offY));
        const el = pageRef.current.querySelector<HTMLElement>(`[data-locked="${index}"]`);
        if (el) { el.style.left = `${newX}px`; el.style.top = `${newY}px`; }
        lockedDragRef.current.lastX = newX;
        lockedDragRef.current.lastY = newY;
      }

      if (resizeRef.current) {
        const { id, startX, startY, origW, origH, zoom: z, isImg } = resizeRef.current;
        const newW = Math.max(40, origW + (e.clientX - startX) / z);
        const d = pageRef.current.querySelector<HTMLElement>(`[data-id="${id}"]`);
        if (d) d.style.width = `${newW}px`;
        if (isImg) {
          const newH = Math.max(20, origH + (e.clientY - startY) / z);
          if (d) d.style.height = `${newH}px`;
          resizeRef.current.lastH = newH;
        }
        resizeRef.current.lastW = newW;
      }
    };

    const onUp = () => {
      if (dragRef.current) {
        const { id, lastX, lastY } = dragRef.current;
        if (lastX !== undefined) onUpdateEl(id, { x: lastX, y: lastY });
        dragRef.current = null;
      }
      if (lockedDragRef.current) {
        const { index, lastX, lastY } = lockedDragRef.current;
        if (lastX !== undefined && onUpdateLockedBlock) onUpdateLockedBlock(index, lastX, lastY);
        lockedDragRef.current = null;
      }
      if (resizeRef.current) {
        const { id, lastW, lastH, isImg } = resizeRef.current;
        if (lastW !== undefined) {
          const p: ElementPatch = { width: lastW };
          if (isImg && lastH !== undefined) (p as Partial<ImageElement>).height = lastH;
          onUpdateEl(id, p);
        }
        resizeRef.current = null;
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [onUpdateEl, onUpdateLockedBlock]);

  const toolbarTitle = titleBarLabel ?? "Letter Composer";

  const ToggleChip = ({
    label,
    active,
    onClick,
  }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      title={active ? `Show ${label}` : `Hide ${label} when printing`}
      style={{
        height: 22,
        padding: "0 8px",
        borderRadius: 5,
        border: "1px solid",
        borderColor: active ? "#ef4444" : "#e5e7eb",
        background: active ? "rgba(239,68,68,0.08)" : "transparent",
        color: active ? "#ef4444" : "#6b7280",
        fontSize: 10,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "'DM Sans',sans-serif",
        display: "flex",
        alignItems: "center",
        gap: 3,
        whiteSpace: "nowrap",
        transition: "all 0.15s",
      }}
    >
      <i className={`pi ${active ? "pi-eye-slash" : "pi-eye"}`} style={{ fontSize: 9 }} />
      {active ? `No ${label}` : label}
    </button>
  );

  return (
    <div className="slh">
      {/* ── Toolbar ── */}
      <div className="slh-bar">
        <span className="slh-bar-title">
          ✦ {toolbarTitle} — {SCHOOL.name}
          {isEditing && <span style={{ marginLeft: "12px", fontSize: "13px", color: "#64748b" }}>(Editing)</span>}
        </span>

        <div className="slh-sep" />

        {isEditing && onNewLetterhead && (
          <button className="tb" onClick={onNewLetterhead} style={{ marginRight: "4px" }}>
            <i className="pi pi-plus" style={{ fontSize: 11 }} /> New
          </button>
        )}

        {!isTemplateMode && (
          <button
            className="tb"
            onClick={() => setTemplateDrawerOpen(true)}
            style={{
              borderColor: "rgba(30,58,138,0.3)",
              background: "rgba(30,58,138,0.07)",
              color: "#1e3a8a",
              marginRight: 4,
            }}
          >
            <i className="pi pi-copy" style={{ fontSize: 11 }} /> Use Template
          </button>
        )}

        <input
          type="text"
          value={letterheadTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Title"
          className="prop-inp"
          style={{ width: 200, height: 27, margin: "0 6px", fontSize: 12 }}
        />

        <button className="tb" onClick={() => onAddEl({ content: "Dear Parents / Carers,", html: "Dear Parents / Carers,", y: 220, fontSize: 13 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Salutation
        </button>
        <button className="tb" onClick={() => onAddEl({ content: "Re: ", html: "<b>Re: </b>", y: 195, fontSize: 13 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Subject
        </button>
        <button className="tb" onClick={() => onAddEl({ content: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }), y: 175, x: 500, width: 255, align: "right", fontSize: 12 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Date
        </button>
        <button className="tb" onClick={() => onAddEl({ content: "Write your paragraph here...", y: 260, width: 620, fontSize: 13 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Paragraph
        </button>
        <button className="tb" style={{ borderColor: "#6ee7b7", color: "#065f46", background: "#f0fdf4" }} onClick={addSignOff}>
          <i className="pi pi-pencil" style={{ fontSize: 11 }} /> Sign-off + Signature
        </button>

        <div className="slh-sep" />

        <select
          className="tb-sel"
          disabled={!sel || sel.type === "image"}
          value={(sel as TextElement | null)?.fontFamily ?? ""}
          onChange={(e) => patchSel({ fontFamily: e.target.value })}
        >
          <option value="'DM Sans',sans-serif">DM Sans</option>
          <option value="'Playfair Display',serif">Playfair Display</option>
          <option value="'Lato',sans-serif">Lato</option>
          <option value="'Merriweather',serif">Merriweather</option>
          <option value="Georgia,serif">Georgia</option>
          <option value="'Courier New',monospace">Courier New</option>
          <option value="'Cambria',serif">Cambria</option>
          <option value="'Calibri',sans-serif">Calibri</option>
          <option value="'Franklin Gothic',sans-serif">Franklin Gothic</option>
          <option value="'Lucida Sans',sans-serif">Lucida Sans</option>
          <option value="'Book Antiqua',serif">Book Antiqua</option>
          <option value="Verdana,sans-serif">Verdana</option>
          <option value="'Rockwell',serif">Rockwell</option>
          <option value="Arial,sans-serif">Arial</option>
          <option value="'Arial Black',sans-serif">Arial Black</option>
          <option value="Impact,sans-serif">Impact</option>
          <option value="'Times New Roman',serif">Times New Roman</option>
        </select>
        <select
          className="tb-sel"
          style={{ width: 50 }}
          disabled={!sel || sel.type === "image"}
          value={(sel as TextElement | null)?.fontSize ?? 13}
          onChange={(e) => handleFontSizeChange(+e.target.value)}
        >
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <button className={`tb${(sel as TextElement | null)?.bold ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ bold: !(sel as TextElement).bold })}>
          <i className="pi pi-bold" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.italic ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ italic: !(sel as TextElement).italic })}>
          <i className="pi pi-italic" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.underline ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ underline: !(sel as TextElement).underline })}>
          <i className="pi pi-underline" style={{ fontSize: 12 }} />
        </button>

        <div className="slh-sep" />

        <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 2, marginRight: 2, whiteSpace: "nowrap" }}>Selection:</span>
        <button className="tb" disabled={!sel || sel.type === "image"} onClick={() => execOnSel("bold")} style={{ fontWeight: 700, fontSize: 12 }}>B</button>
        <button className="tb" disabled={!sel || sel.type === "image"} onClick={() => execOnSel("italic")} style={{ fontStyle: "italic", fontSize: 12 }}>I</button>
        <button className="tb" disabled={!sel || sel.type === "image"} onClick={() => execOnSel("underline")} style={{ textDecoration: "underline", fontSize: 12 }}>U</button>

        <div className="slh-sep" />
        <span style={{ fontSize: 10, color: "#9ca3af", marginRight: 2, whiteSpace: "nowrap" }}>List:</span>
        <button
          className="tb"
          disabled={!sel || sel.type === "image"}
          onClick={() => execOnSel("insertUnorderedList")}
          title="Bullet list"
          style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.5px" }}
        >
          • ≡
        </button>
        <button
          className="tb"
          disabled={!sel || sel.type === "image"}
          onClick={() => execOnSel("insertOrderedList")}
          title="Numbered list"
          style={{ fontSize: 11, fontWeight: 700 }}
        >
          1. ≡
        </button>

        <div className="slh-sep" />

        <button className={`tb${(sel as TextElement | null)?.align === "left" ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ align: "left" })}>
          <i className="pi pi-align-left" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.align === "center" ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ align: "center" })}>
          <i className="pi pi-align-center" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.align === "right" ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ align: "right" })}>
          <i className="pi pi-align-right" style={{ fontSize: 12 }} />
        </button>
        <button
          className={`tb${(sel as TextElement | null)?.align === "justify" ? " on" : ""}`}
          disabled={!sel || sel.type === "image"}
          onClick={() => patchSel({ align: "justify" })}
          title="Justify"
          style={{ fontSize: 14, fontWeight: 900, letterSpacing: -1 }}
        >
          ≡
        </button>

        <div className="slh-sep" />
        <button className="tb" disabled={!sel} onClick={() => { if (sel) patchSel({ x: Math.round((PAGE_W - sel.width) / 2) }); }}>
          <i className="pi pi-align-center" style={{ fontSize: 12 }} /> Centre
        </button>
        <div className="slh-sep" />
        <input type="color" className="tb-color" disabled={!sel || sel.type === "image"} value={(sel as TextElement | null)?.color ?? "#000000"} onChange={(e) => patchSel({ color: e.target.value })} />
        <div className="slh-sep" />
        <button className="tb red" disabled={!sel} onClick={() => { if (sel) onDeleteEl(sel.id); }}>
          <i className="pi pi-trash" style={{ fontSize: 12 }} /> Delete
        </button>

        <div style={{ flex: 1 }} />

        <div style={{ display: "flex", alignItems: "center", gap: 4, marginRight: 6 }}>
          <ToggleChip
            label="Header"
            active={hideHeader}
            onClick={() => onHideHeaderChange?.(!hideHeader)}
          />
          <ToggleChip
            label="Footer"
            active={hideFooter}
            onClick={() => onHideFooterChange?.(!hideFooter)}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 1, marginRight: 6, background: "#f8faff", border: "1px solid #e5e7eb", borderRadius: 7, padding: "2px 4px" }}>
          <button className="tb" style={{ height: 23, width: 23, padding: 0, justifyContent: "center" }} onClick={() => {
            const idx = ZOOM_STEPS.indexOf(zoom);
            if (idx > 0) onZoomChange?.(ZOOM_STEPS[idx - 1]);
            else { const prev = ZOOM_STEPS.filter(s => s < zoom); if (prev.length) onZoomChange?.(prev[prev.length - 1]); }
          }}>
            <i className="pi pi-search-minus" style={{ fontSize: 12 }} />
          </button>
          <button style={{ height: 23, minWidth: 40, padding: "0 4px", border: "none", background: "transparent", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#1e3a8a", fontFamily: "'DM Sans',sans-serif", borderRadius: 4 }} onClick={() => onZoomChange?.(0.75)}>
            {Math.round(zoom * 100)}%
          </button>
          <button className="tb" style={{ height: 23, width: 23, padding: 0, justifyContent: "center" }} onClick={() => {
            const idx = ZOOM_STEPS.indexOf(zoom);
            if (idx !== -1 && idx < ZOOM_STEPS.length - 1) onZoomChange?.(ZOOM_STEPS[idx + 1]);
            else { const next = ZOOM_STEPS.filter(s => s > zoom); if (next.length) onZoomChange?.(next[0]); }
          }}>
            <i className="pi pi-search-plus" style={{ fontSize: 12 }} />
          </button>
        </div>

        <div className="slh-sep" />

        {isTemplateMode ? (
          <button className="btn-print" disabled={printing} onClick={onSaveOnly}>
            {printing
              ? <><i className="pi pi-spinner pi-spin" style={{ fontSize: 13 }} /> Saving…</>
              : <><i className="pi pi-save" style={{ fontSize: 13, color: "white" }} /> Save</>
            }
          </button>
        ) : isDirty ? (
          <button className="btn-print" disabled={printing} onClick={onSaveAndPrint}>
            {printing
              ? <><i className="pi pi-spinner pi-spin" style={{ fontSize: 13 }} /> Saving & Printing…</>
              : <><i className="pi pi-print" style={{ fontSize: 13, color: "white" }} /> Save & Print</>
            }
          </button>
        ) : (
          <button className="btn-print" disabled={printing} onClick={onPrintOnly}>
            <i className="pi pi-print" style={{ fontSize: 13, color: "white" }} /> Print
          </button>
        )}
      </div>

      {/* ── Body ── */}
      <div className="slh-body">
        {/* ── Left panel ── */}
        <div className="slh-left">
          <div className="panel-head">
            <h3>Elements</h3>
            <p>Hover → grab bar to drag</p>
          </div>
          <div className="fmt-hint">
            💡 Select text → B/I/U toolbar pops up<br />
            🖊 Signature slot → click to upload image
          </div>
          <div className="el-list">
            {elements.length === 0 && (
              <div style={{ padding: "18px 8px", textAlign: "center", color: "#9ca3af", fontSize: 11, lineHeight: 1.7 }}>
                No elements yet.<br />Use toolbar to add<br />or click <strong>Use Template</strong>.
              </div>
            )}
            {elements.map((el) => (
              <div
                key={el.id}
                className={`el-item${el.type === "image" ? " img-el" : ""}${el.id === selId ? " sel" : ""}`}
                onClick={() => { onSetSelId(el.id); setSelLockedIdx(null); }}
                style={{ display: "flex", alignItems: "flex-start", gap: 4 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="el-name" style={{ display: "flex", alignItems: "center", gap: 4, color: el.type === "image" ? "#065f46" : "#374151" }}>
                    {el.type === "image" && <i className="pi pi-image" style={{ fontSize: 10, color: "#10b981" }} />}
                    {el.type === "image"
                      ? (el as ImageElement).src ? "✓ Signature" : "⬜ Signature (empty)"
                      : el.content.substring(0, 22) + (el.content.length > 22 ? "…" : "")}
                  </div>
                  <div className="el-meta">x:{Math.round(el.x)} y:{Math.round(el.y)}</div>
                </div>
                <button
                  title="Delete"
                  onClick={(e) => { e.stopPropagation(); onDeleteEl(el.id); }}
                  style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 4, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", padding: 0, marginTop: 1 }}
                >
                  <i className="pi pi-trash" style={{ fontSize: 11 }} />
                </button>
              </div>
            ))}
          </div>
          <button className="add-btn" onClick={() => onAddEl()}>
            <i className="pi pi-plus" style={{ fontSize: 11 }} /> Add text block
          </button>
          <button className="add-btn green" onClick={() => onAddImgEl({ x: 80, y: 700, width: 180, height: 70 })}>
            <i className="pi pi-image" style={{ fontSize: 11 }} /> Add signature slot
          </button>
          {!isTemplateMode && (
            <button
              className="add-btn"
              onClick={() => setTemplateDrawerOpen(true)}
              style={{ borderColor: "#bfdbfe", color: "#1e3a8a", marginTop: 4 }}
            >
              <i className="pi pi-copy" style={{ fontSize: 11 }} /> Use Template
            </button>
          )}
        </div>

        {/* ── Canvas ── */}
        <div ref={canvasRef} className="slh-canvas">
          <div style={{ width: PAGE_W * zoom, height: PAGE_H * zoom, flexShrink: 0, marginBottom: 20 }}>
            <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left", width: PAGE_W, height: PAGE_H }}>
              <div
                ref={pageRef}
                className="slh-page"
                style={{ width: PAGE_W, height: PAGE_H }}
                onMouseDown={(e) => {
                  if (e.target === pageRef.current) {
                    onSetSelId(null);
                    setSelLockedIdx(null);
                  }
                }}
              >
                {/* Header */}
                {!hideHeader && (
                  <img ref={headerImgRef} className="pg-header" src={SCHOOL.headerImage} alt="header" draggable={false} />
                )}
                {hideHeader && (
                  <div style={{
                    width: "100%",
                    height: 8,
                    background: "repeating-linear-gradient(90deg,#fee2e2 0,#fee2e2 10px,#fff1f2 10px,#fff1f2 20px)",
                    flexShrink: 0,
                    borderBottom: "1px dashed #fca5a5",
                  }} title="Header hidden for print" />
                )}

                {/* Footer */}
                {!hideFooter && (
                  <img ref={footerImgRef} className="pg-footer" src={SCHOOL.footerImage} alt="footer" draggable={false} />
                )}
                {hideFooter && (
                  <div style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: 8,
                    background: "repeating-linear-gradient(90deg,#fee2e2 0,#fee2e2 10px,#fff1f2 10px,#fff1f2 20px)",
                    borderTop: "1px dashed #fca5a5",
                  }} title="Footer hidden for print" />
                )}

                <div className="pg-layer">
                  {elements.map((el) => (
                    <div
                      key={el.id}
                      data-id={el.id}
                      className={`slh-el${el.type === "image" ? " img-el" : ""}${el.id === selId ? " sel" : ""}`}
                      style={{
                        left: el.x,
                        top: el.y,
                        width: el.width,
                        height: el.type === "image" ? (el as ImageElement).height : undefined,
                        position: "absolute",
                      }}
                      onClick={(e) => { e.stopPropagation(); onSetSelId(el.id); setSelLockedIdx(null); }}
                    >
                      <div
                        className={`el-handle${el.type === "image" ? " green" : ""}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSetSelId(el.id);
                          setSelLockedIdx(null);
                          const rect = pageRef.current!.getBoundingClientRect();
                          dragRef.current = { id: el.id, offX: (e.clientX - rect.left) / zoom - el.x, offY: (e.clientY - rect.top) / zoom - el.y, zoom };
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <i className="pi pi-arrows-alt" style={{ fontSize: 9, color: "rgba(255,255,255,0.8)" }} />
                          <span className="el-handle-label">
                            {el.type === "image" ? "✦ Signature image" : el.content.substring(0, 22)}
                          </span>
                        </span>
                        <button className="el-handle-del" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDeleteEl(el.id); }}>✕</button>
                      </div>
                      <div className="el-box">
                        {el.type === "image" ? (
                          <div className="sig-box" onClick={() => sigFileRef.current?.click()}>
                            {(el as ImageElement).src
                              ? <img className="sig-img" src={(el as ImageElement).src!} alt="signature" draggable={false} />
                              : <div className="sig-drop">Click or drop signature image</div>
                            }
                          </div>
                        ) : (
                          <EditableText key={el.id} el={el as TextElement} isSelected={el.id === selId} onUpdate={onUpdateEl} savedSelRef={savedSelRef} />
                        )}
                        {el.id === selId && (
                          el.type === "image" ? (
                            <div
                              className="el-resize-img"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                resizeRef.current = { id: el.id, startX: e.clientX, startY: e.clientY, origW: el.width, origH: (el as ImageElement).height, zoom, isImg: true };
                              }}
                            />
                          ) : (
                            <div
                              className="el-resize"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                resizeRef.current = { id: el.id, startX: e.clientX, startY: e.clientY, origW: el.width, origH: 0, zoom, isImg: false };
                              }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  ))}

                  {lockedBlocks.map((b, i) => {
                    const isSerial = b.label.toLowerCase().includes("s. no") || b.label.toLowerCase().includes("serial");
                    return (
                      <div
                        key={`locked-${i}`}
                        data-locked={i}
                        style={{
                          position: "absolute",
                          left: b.x,
                          top: b.y,
                          width: b.width,
                          fontSize: 13,
                          fontFamily: "'DM Sans',sans-serif",
                          color: "#1e3a8a",
                          fontWeight: 600,
                          lineHeight: 1.6,
                          padding: "4px 6px",
                          userSelect: "none",
                          zIndex: 3,
                          cursor: "grab",
                          outline: selLockedIdx === i ? "1.5px dashed #3b82f6" : "1.5px dashed rgba(40,85,174,0.30)",
                          borderRadius: 3,
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelLockedIdx(i);
                          onSetSelId(null);
                          const rect = pageRef.current!.getBoundingClientRect();
                          lockedDragRef.current = { index: i, offX: (e.clientX - rect.left) / zoom - b.x, offY: (e.clientY - rect.top) / zoom - b.y, zoom };
                        }}
                      >
                        {b.label}
                        {isSerial && currentLetterheadId && (
                          <span style={{ marginLeft: "8px", fontWeight: 400, color: "#1e3a8a" }}>
                            {currentLetterheadId}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="slh-right">
          {sel ? (
            sel.type === "image" ? (
              <div className="props">
                <div className="prop-sec green">Signature</div>
                <div className="sig-panel">
                  <div className="sig-panel-title">
                    <i className="pi pi-image" style={{ fontSize: 11, color: "#065f46" }} /> Upload signature image
                  </div>
                  {(sel as ImageElement).src ? (
                    <>
                      <img className="sig-thumb" src={(sel as ImageElement).src!} alt="signature preview" />
                      <button className="sig-upload-btn" onClick={() => sigFileRef.current?.click()}>Replace image</button>
                      <button className="sig-clear-btn" onClick={() => onUpdateEl(sel.id, { src: null })}>Remove</button>
                    </>
                  ) : (
                    <button className="sig-upload-btn" onClick={() => sigFileRef.current?.click()}>Choose file…</button>
                  )}
                </div>
                <input ref={sigFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleSigFile} />
                <div className="prop-sec green">Position &amp; Size</div>
                <div className="prop-lbl">X (px)</div>
                <input className="prop-inp" type="number" value={Math.round(sel.x)} onChange={(e) => patchSel({ x: Math.round(+e.target.value) })} />
                <div className="prop-lbl">Y (px)</div>
                <input className="prop-inp" type="number" value={Math.round(sel.y)} onChange={(e) => patchSel({ y: Math.round(+e.target.value) })} />
                <div className="prop-lbl">Width (px)</div>
                <input className="prop-inp" type="number" value={Math.round(sel.width)} onChange={(e) => patchSel({ width: Math.max(40, Math.round(+e.target.value)) })} />
                <div className="prop-lbl">Height (px)</div>
                <input className="prop-inp" type="number" value={Math.round((sel as ImageElement).height)} onChange={(e) => patchSel({ height: Math.max(20, Math.round(+e.target.value)) })} />
                <div className="prop-sec green">Align</div>
                <button className="prop-btn" style={{ width: "100%", marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }} onClick={() => { if (sel) patchSel({ x: Math.round((PAGE_W - sel.width) / 2) }); }}>
                  <i className="pi pi-align-center" style={{ fontSize: 11 }} /> Centre on Page
                </button>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
                  <button className="prop-btn" onClick={() => { if (sel) patchSel({ x: 40 }); }}>← Left</button>
                  <button className="prop-btn" onClick={() => { if (sel) patchSel({ x: Math.round(PAGE_W - sel.width - 40) }); }}>Right →</button>
                </div>
                <div className="prop-sec">Actions</div>
                <button className="prop-btn" style={{ width: "100%", marginBottom: 4 }} onClick={() => onDuplicateEl(sel.id)}>Duplicate</button>
                <button className="prop-btn del" style={{ width: "100%" }} onClick={() => onDeleteEl(sel.id)}>Delete</button>
              </div>
            ) : (
              <div className="props">
                <div className="prop-sec">Position</div>
                <div className="prop-lbl">X (px)</div>
                <input className="prop-inp" type="number" value={Math.round(sel.x)} onChange={(e) => patchSel({ x: Math.round(+e.target.value) })} />
                <div className="prop-lbl">Y (px)</div>
                <input className="prop-inp" type="number" value={Math.round(sel.y)} onChange={(e) => patchSel({ y: Math.round(+e.target.value) })} />
                <div className="prop-lbl">Width (px)</div>
                <input className="prop-inp" type="number" value={Math.round(sel.width)} onChange={(e) => patchSel({ width: Math.max(60, Math.round(+e.target.value)) })} />
                <div className="prop-sec">Typography</div>
                <div className="prop-lbl">Font size (px)</div>
                <input className="prop-inp" type="number" value={(sel as TextElement).fontSize} onChange={(e) => patchSel({ fontSize: +e.target.value })} />
                <div className="prop-lbl">Colour</div>
                <input className="prop-inp" type="color" style={{ height: 30, padding: 2 }} value={(sel as TextElement).color} onChange={(e) => patchSel({ color: e.target.value })} />
                <div className="prop-sec">Text Align</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, marginBottom: 8 }}>
                  <button className={`prop-btn${(sel as TextElement).align === "left" ? " on" : ""}`} onClick={() => patchSel({ align: "left" })}>L</button>
                  <button className={`prop-btn${(sel as TextElement).align === "center" ? " on" : ""}`} onClick={() => patchSel({ align: "center" })}>C</button>
                  <button className={`prop-btn${(sel as TextElement).align === "right" ? " on" : ""}`} onClick={() => patchSel({ align: "right" })}>R</button>
                  <button className={`prop-btn${(sel as TextElement).align === "justify" ? " on" : ""}`} onClick={() => patchSel({ align: "justify" })} title="Justify">≡</button>
                </div>
                <div className="prop-sec">Horizontal Align</div>
                <div className="prop-row">
                  <button className="prop-btn" onClick={() => patchSel({ x: 40 })}>← Left</button>
                  <button className="prop-btn" onClick={() => patchSel({ x: Math.round((PAGE_W - sel.width) / 2) })}>Center</button>
                </div>
                <button className="prop-btn" style={{ width: "100%", marginBottom: 5 }} onClick={() => patchSel({ x: PAGE_W - sel.width - 40 })}>Right →</button>
                <div className="prop-sec">Actions</div>
                <button className="prop-btn" style={{ width: "100%", marginBottom: 4 }} onClick={() => onDuplicateEl(sel.id)}>Duplicate</button>
                <button className="prop-btn del" style={{ width: "100%" }} onClick={() => onDeleteEl(sel.id)}>Delete</button>
              </div>
            )
          ) : selLockedIdx !== null && lockedBlocks[selLockedIdx] ? (
            <div className="props">
              <div className="prop-sec">🔒 {lockedBlocks[selLockedIdx].label}</div>
              <div className="prop-lbl">X (px)</div>
              <input className="prop-inp" type="number" value={Math.round(lockedBlocks[selLockedIdx].x)} onChange={(e) => onUpdateLockedBlock?.(selLockedIdx, +e.target.value, lockedBlocks[selLockedIdx].y)} />
              <div className="prop-lbl">Y (px)</div>
              <input className="prop-inp" type="number" value={Math.round(lockedBlocks[selLockedIdx].y)} onChange={(e) => onUpdateLockedBlock?.(selLockedIdx, lockedBlocks[selLockedIdx].x, +e.target.value)} />
              <div className="prop-lbl">Width (px)</div>
              <input className="prop-inp" type="number" value={Math.round(lockedBlocks[selLockedIdx].width)} onChange={(e) => onUpdateLockedBlock?.(selLockedIdx, lockedBlocks[selLockedIdx].x, lockedBlocks[selLockedIdx].y, +e.target.value)} />
              <div className="prop-sec">Horizontal Align</div>
              <div className="prop-row">
                <button className="prop-btn" onClick={() => onUpdateLockedBlock?.(selLockedIdx, 40, lockedBlocks[selLockedIdx].y)}>← Left</button>
                <button className="prop-btn" onClick={() => onUpdateLockedBlock?.(selLockedIdx, Math.round((PAGE_W - lockedBlocks[selLockedIdx].width) / 2), lockedBlocks[selLockedIdx].y)}>Center</button>
              </div>
              <button className="prop-btn" style={{ width: "100%", marginBottom: 5 }} onClick={() => onUpdateLockedBlock?.(selLockedIdx, PAGE_W - lockedBlocks[selLockedIdx].width - 40, lockedBlocks[selLockedIdx].y)}>Right →</button>
              <button className="prop-btn" style={{ width: "100%", marginBottom: 5 }} onClick={() => {
                const serial = lockedBlocks.findIndex(b => b.label.toLowerCase().includes("no"));
                const date = lockedBlocks.findIndex(b => b.label.toLowerCase().includes("date"));
                const sharedY = lockedBlocks[selLockedIdx!].y;
                if (serial !== -1) onUpdateLockedBlock?.(serial, 40, sharedY);
                if (date !== -1) onUpdateLockedBlock?.(date, PAGE_W - lockedBlocks[date].width - 40, sharedY);
              }}>← S.No. Left &nbsp;|&nbsp; Date Right →</button>
              {lockedBlocks.length > 1 && (
                <>
                  <div className="prop-sec">Align With</div>
                  <div className="prop-row">
                    {lockedBlocks.map((b, i) => i !== selLockedIdx ? (
                      <button key={i} className="prop-btn" onClick={() => onUpdateLockedBlock?.(selLockedIdx, b.x, lockedBlocks[selLockedIdx].y)}>"{b.label}" X</button>
                    ) : null)}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="no-sel">Select an element to see its properties here.</div>
          )}
        </div>
      </div>

      {/* ── Template Drawer ── */}
      <TemplateDrawer
        open={templateDrawerOpen}
        onClose={() => { setTemplateDrawerOpen(false); setReplaceConfirm(null); }}
        onApplied={() => setTemplateDrawerOpen(false)}
        onApply={handleTemplateApply}
      />

      {/* ── Replace confirmation modal ── */}
      {replaceConfirm && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <div style={{
            background: "white",
            borderRadius: 16,
            padding: 28,
            maxWidth: 380,
            width: "90%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            fontFamily: "'DM Sans',sans-serif",
          }}>
            <div style={{ fontSize: 22, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1f2937", marginBottom: 8 }}>
              Replace existing elements?
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
              This will replace all current canvas elements with those from the selected template. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setReplaceConfirm(null)}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                  color: "#374151",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => doApplyTemplate(replaceConfirm)}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: "linear-gradient(135deg,#1e3a8a,#3b82f6)",
                  color: "white",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                Yes, Replace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterEditor;